import { useOrchestrationFabrics } from '@common/Fabric/hooks';
import { useOrchestrationMultiFabricQuery } from '@common/Fabric/OrchestrationFabric/api';
import { useOrchestrationHistoryApis } from '@common/Fabric/OrchestrationFabric/useOrchestrationHistoryApis';
import { useUserSQLProjects } from '@common/projectHooks/useUserSQLProjects';
import { Entity, getAppEntityName } from '@common/types/Entity';
import { getSqlIDEUrl } from '@common/url';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { useIsVerticalApp } from '@common/VerticalApps/utils';
import { ArrowNarrowUpRightIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button } from '@prophecy/ui-v3/Button';
import { StyledLabel } from '@prophecy/ui-v3/Input';
import { Stack } from '@prophecy/ui-v3/Layout';
import { DateRangeInput } from '@prophecy/ui-v3/Picker/inputVariants/DateRangeInput';
import { DateRange, PICKER_VARIANT } from '@prophecy/ui-v3/Picker/types';
import { Select } from '@prophecy/ui-v3/Select';
import { Table } from '@prophecy/ui-v3/Table';
import { Tooltip } from '@prophecy/ui-v3/Tooltip';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { formattedDateTime, getDuration } from '@prophecy/utils/date';
import { useSearchParamState } from '@prophecy/utils/react/hooks';
import { parseJSON } from '@prophecy/utils/string';
import { useGlobalVariables } from 'frontend/core/src/context/globalConfig/securedConfigHook';
import { SqlPipelineRunType } from 'frontend/core/src/data/apis/types';
import { SlidingDetailsDialog } from 'frontend/core/src/generic-components/Logger/SlidingDetailsDialog';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { castArray } from '../../../data/util';
import TriggerBagdes from '../Components/TriggerBagdes';
import { SQLPipelineObservationLayout } from './Layout';
import { StyledSelect } from './styled';
import { SQLPipelineRunStatus } from './types';
import { getRunStatusBadge, withEllipsis } from './utils';

const StyledRunTypeSelect = styled(Select)`
  min-width: 300px;
`;

const Link = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${theme.spaces.x4};
` as unknown as typeof Button;
function useFetchRuns({
  fabricId,
  projectId,
  minTime,
  maxTime,
  runTypes
}: {
  fabricId?: string | string[];
  projectId?: string | string[];
  pipelineName?: string;
  minTime?: Date;
  maxTime?: Date;
  runTypes?: SqlPipelineRunType[];
}) {
  const { fabrics, isLoading: isJWTLoading, fabricsMap } = useOrchestrationFabrics();

  const { getPipelineRuns } = useOrchestrationHistoryApis();
  const { data, isInitialLoading: isLoading } = useOrchestrationMultiFabricQuery(
    ['orchestration-pipeline-runs', fabricId, projectId, minTime, maxTime, runTypes],
    () =>
      getPipelineRuns({
        projectIds: projectId ? castArray(projectId) : undefined,
        fabricIds: fabricId ? castArray(fabricId) : undefined,
        minTime,
        maxTime,
        runTypes
      })
  );

  const runs = useMemo(() => {
    return castArray(data?.data.history).map((run) => ({
      ...run,
      sensorConfigDetails: parseJSON(run.sensorConfigDetails, [])
    }));
  }, [data]);

  return {
    runs,
    isLoading: isLoading || isJWTLoading,
    fabrics,
    fabricsMap
  };
}

const All_RUN_TYPES = [
  SqlPipelineRunType.Scheduled,
  SqlPipelineRunType.InteractivePipelineRun,
  SqlPipelineRunType.InteractiveAppRun,
  SqlPipelineRunType.AppSchedule,
  SqlPipelineRunType.APIPipelineRun
];

// TODO: @ameerthehacker add pagination
export function PipelineRuns() {
  const [fabricId, setFabricId] = useSearchParamState('prFabricId');
  const [projectId, setProjectId] = useSearchParamState('prProjectId');
  const { projectsMap, isLoading: isProjectsLoading, projects } = useUserSQLProjects();
  const [messageToShow, setMessageToShow] = useState<{ message: string; type: 'error' | 'warning' } | null>(null);
  const [dateRange, setDateRange] = useSearchParamState<[Date | undefined, Date | undefined]>('prDateRange', {
    initialValue: [undefined, undefined],
    serialize: (value) => value.map((date) => date?.toISOString()).join(','),
    deserialize: (value) =>
      value.split(',').map((date) => (date ? new Date(date) : undefined)) as [Date | undefined, Date | undefined]
  });
  const [runTypes, setRunTypes] = useSearchParamState('prRunTypes', {
    initialValue: All_RUN_TYPES,
    serialize: (value) => value.join(','),
    deserialize: (value) => (value ? value.split(',') : FROZEN_EMPTY_ARRAY) as SqlPipelineRunType[]
  });

  const {
    isLoading: isRunsLoading,
    runs,
    fabrics,
    fabricsMap
  } = useFetchRuns({
    fabricId,
    projectId: projectId || projects.map((project) => project._id),
    minTime: dateRange?.[0] ? new Date(dateRange[0].setHours(0, 0, 0, 0)) : undefined,
    maxTime: dateRange?.[1] ? new Date(dateRange[1].setHours(23, 59, 59)) : undefined,
    runTypes: !runTypes || runTypes.length === 0 ? All_RUN_TYPES : runTypes
  });

  const { isHistoricalRunSnapshotsEnabled } = useGlobalVariables();
  const isVerticalApp = useIsVerticalApp();

  return (
    <SQLPipelineObservationLayout title='Run History' count={runs.length}>
      <Stack direction='horizontal' gap={theme.spaces.x20}>
        <Stack gap={theme.spaces.x6}>
          <StyledLabel level='md'>Date Range</StyledLabel>
          <DateRangeInput
            dateVariant={PICKER_VARIANT.DATE_RANGE}
            inputContainerSize='m'
            inputSize='s'
            showActionFooter
            pickerProps={{
              showPresets: true,
              allowClear: true
            }}
            value={dateRange}
            onChange={setDateRange as (value: DateRange | [string, string]) => unknown}
            data-test-id='sql-pipeline-runs-date-range-picker'
          />
        </Stack>
        {!isVerticalApp && (
          <StyledSelect
            label='Fabric'
            allowClear
            placeholder='Select Fabric'
            data-test-id='sql-pipeline-runs-fabric-select'
            options={fabrics.map((fabric) => ({
              label: fabric.name,
              value: fabric.id
            }))}
            value={fabricId}
            onChange={setFabricId}
          />
        )}
        <StyledSelect
          label='Project'
          allowClear
          placeholder='Select Project'
          options={projects.map((project) => ({
            label: project.name,
            value: project._id
          }))}
          value={projectId}
          onChange={setProjectId}
          data-test-id='sql-pipeline-runs-project-select'
        />
        <StyledRunTypeSelect
          allowClear
          label='Run Type'
          mode='multiple'
          value={runTypes}
          onChange={setRunTypes}
          options={[
            {
              label: 'Scheduled',
              value: SqlPipelineRunType.Scheduled
            },
            {
              label: 'Pipeline Run',
              value: SqlPipelineRunType.InteractivePipelineRun
            },
            {
              label: `Scheduled ${getAppEntityName(Entity.App)} Run`,
              value: SqlPipelineRunType.AppSchedule
            },
            {
              label: `${getAppEntityName(Entity.App)} Run`,
              value: SqlPipelineRunType.InteractiveAppRun
            },
            {
              label: 'API Pipeline Run',
              value: SqlPipelineRunType.APIPipelineRun
            }
          ]}
          data-test-id='sql-pipeline-run-type-select'
        />
      </Stack>

      <Table
        loading={isRunsLoading || isProjectsLoading}
        virtualize
        height='100%'
        placeholderRows={50}
        width='100%'
        fixedHeader
        rowKey='runId'
        data-test-id='sql-pipeline-runs-table'
        columns={[
          {
            key: 'runId',
            title: 'ID',
            width: '45px',
            render: (_, record) => {
              return (
                <Tooltip title={record.runId || '--'}>
                  <span>#</span>
                </Tooltip>
              );
            }
          },
          {
            title: 'Fabric',
            render: (_, record) => {
              const fabric = fabricsMap.get(record.fabricId);
              return withEllipsis(fabric?.name || record.fabricId);
            },
            key: 'fabric'
          },
          {
            title: 'Pipeline',
            dataIndex: 'pipelineName',
            key: 'pipelineName',
            render: withEllipsis
          },
          {
            title: 'Project',
            render: (_, record) => {
              return withEllipsis(projectsMap.get(record.projectId) || record.projectId);
            },
            key: 'projectId'
          },
          {
            title: 'Run Type',
            width: '20%',
            render: (_, record) => {
              return (
                <TriggerBagdes
                  cron={record.cron}
                  sensorConfigDetails={record.sensorConfigDetails}
                  runType={
                    record.appName && record.runType === SqlPipelineRunType.InteractivePipelineRun
                      ? SqlPipelineRunType.InteractiveAppRun
                      : record.runType
                  }
                  appName={record.appName}
                  instanceName={record.instanceName}
                />
              );
            },
            key: 'runType'
          },
          {
            title: 'Start Time',
            render: (_, record) => {
              return renderColumnWithEllipsis(
                formattedDateTime(record.startTime, {
                  month: 'short',
                  day: 'numeric',
                  year: new Date().getFullYear() === new Date(record.startTime).getFullYear() ? undefined : 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                  second: 'numeric'
                })
              );
            },
            key: 'startTime'
          },
          {
            title: 'End Time',
            width: '120px',
            render: (_, record) => {
              if (!record.endTime || record.runStatus === SQLPipelineRunStatus.RUNNING) return 'N/A';
              const startDate = new Date(record.startTime);
              const endDate = new Date(record.endTime);
              const shouldShowDayInfo =
                startDate.getDate() === endDate.getDate() &&
                startDate.getMonth() === endDate.getMonth() &&
                startDate.getFullYear() === endDate.getFullYear() &&
                startDate.getFullYear() === new Date().getFullYear();

              return renderColumnWithEllipsis(
                formattedDateTime(record.endTime, {
                  month: shouldShowDayInfo ? undefined : 'short',
                  day: shouldShowDayInfo ? undefined : 'numeric',
                  year: shouldShowDayInfo ? undefined : 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                  second: 'numeric'
                })
              );
            },
            key: 'endTime'
          },
          {
            title: 'Duration',
            width: '100px',
            render: (_, record) => {
              const seconds = Math.round(record.duration_ns / 1000000000);

              return renderColumnWithEllipsis(
                record.runStatus === SQLPipelineRunStatus.RUNNING ? 'N/A' : getDuration(seconds, true)
              );
            },
            key: 'duration'
          },
          {
            title: 'Result',
            width: '100px',
            render: (_, record) => {
              const isWarning = record.runStatus === SQLPipelineRunStatus.WARNING;
              return getRunStatusBadge(record.runStatus, record.errorMessage, () =>
                setMessageToShow({
                  message: record.errorMessage || '',
                  type: isWarning ? 'warning' : 'error'
                })
              );
            },
            key: 'result'
          },
          ...(isHistoricalRunSnapshotsEnabled
            ? [
                {
                  title: 'Logs',
                  render: (_: unknown, record: (typeof runs)[number]) => {
                    const isApp =
                      record.runType === SqlPipelineRunType.InteractiveAppRun ||
                      record.runType === SqlPipelineRunType.AppSchedule;
                    const isRunning = record.runStatus === SQLPipelineRunStatus.RUNNING;
                    const disable = !isHistoricalRunSnapshotsEnabled || !record.hasRuntimeLogs || isApp || isRunning;
                    if (disable) {
                      return renderColumnWithEllipsis('No logs available');
                    }
                    return (
                      <Link
                        variant='linkGray'
                        elementType='link'
                        tooltip='Click to open pipeline in Historical mode and see all past runs'
                        data-test-id='sql-pipeline-runs-logs-link'
                        aria-label='View Logs'
                        to={getSqlIDEUrl({
                          uid: record.projectId,
                          entity: Entity.Pipeline,
                          name: record.pipelineName,
                          runId: record.runId,
                          runType:
                            record.runType === SqlPipelineRunType.InteractivePipelineRun
                              ? 'pipelineRun'
                              : record.runType === SqlPipelineRunType.InteractiveAppRun
                                ? 'appRun'
                                : undefined
                        })}
                        icon={<ArrowNarrowUpRightIcon type='default' size='xs' />}
                        iconPlacement='right'
                        size='xs'>
                        See logs
                      </Link>
                    );
                  },
                  width: '100px',
                  key: 'logs'
                }
              ]
            : [])
        ]}
        dataSource={runs}
      />
      {messageToShow && (
        <SlidingDetailsDialog
          title={messageToShow.type === 'warning' ? 'Warning Details' : 'Error Details'}
          onClose={() => setMessageToShow(null)}
          message={messageToShow.message}
          type={messageToShow.type}
        />
      )}
    </SQLPipelineObservationLayout>
  );
}
